import React from 'react';
import { useTranslation } from 'react-i18next';
import { Currency } from '../../types/api';
import { formatPrice } from '../../utils/priceFormat';

interface SubscriptionDetailsInfoProps {
  subscriptionType: 'monthly' | 'yearly' | 'trial';
  calculatedTotal: number;
  currency: string;
  availableCurrencies: Currency[];
}

const SubscriptionDetailsInfo: React.FC<SubscriptionDetailsInfoProps> = ({
  subscriptionType,
  calculatedTotal,
  currency,
  availableCurrencies
}) => {
  const { t } = useTranslation();

  const getCurrencySymbol = () => {
    return availableCurrencies.find((c: Currency) => c.code === currency)?.symbol || 
           (currency === 'thb' ? '฿' : currency === 'usd' ? '$' : currency);
  };

  const getNextPaymentDate = () => {
    const today = new Date();
    const nextDate = new Date(today);
    if (subscriptionType === 'monthly') {
      // Use 28 days (4 weeks) for monthly subscriptions instead of a calendar month
      nextDate.setDate(today.getDate() + 28);
    } else {
      nextDate.setFullYear(today.getFullYear() + 1);
    }
    return nextDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="text-sm font-medium text-gray-800 mb-2">
        {t('subscription.orderSummary.subscriptionDetails')}
      </div>
      <div className="text-xs text-gray-600 space-y-2">
        <div className="flex justify-between">
          <span>{t('subscription.orderSummary.subscriptionPeriod')}</span>
          <span className="font-medium">
            {subscriptionType === 'trial'
              ? t('subscription.orderSummary.trialPeriod', 'One-time trial')
              : (subscriptionType === 'monthly' 
                ? t('subscription.orderSummary.monthlyPeriod', 'billing every 4 weeks') 
                : t('subscription.orderSummary.yearlyPeriod', 'billing annually'))}
          </span>
        </div>
        <div className="flex justify-between">
          <span>
            {subscriptionType === 'trial' 
              ? t('subscription.orderSummary.totalCost', 'Total cost')
              : t('subscription.orderSummary.averageCostPerDelivery')}
          </span>
          <span className="font-medium">
            {subscriptionType === 'trial'
              ? `${getCurrencySymbol()}${formatPrice(calculatedTotal, currency)}`
              : `${getCurrencySymbol()}${formatPrice(calculatedTotal / (subscriptionType === 'monthly' ? 4 : 52), currency)}`}
          </span>
        </div>
        {subscriptionType !== 'trial' && (
          <div className="flex justify-between">
            <span>{t('subscription.orderSummary.nextPaymentDate')}</span>
            <span className="font-medium">
              {getNextPaymentDate()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetailsInfo; 